import { useState, type ReactElement } from 'react';

import type { StreamState, TraceEntry } from '@harness/stream-core';

import { now } from '../lib/environment.ts';
import { MetricsStrip } from './metrics-strip.tsx';
import { TraceComparison } from './trace-comparison.tsx';

/**
 * Colour follows the same one meaning rule the rest of the system uses: fault
 * for anything that lost or broke data, hold for anything that waited or
 * retried, plain ink for the ordinary frames. A frame list where every line
 * is the same weight is a list nobody reads.
 */
function entryTone(entry: TraceEntry): string {
  switch (entry.kind) {
    case 'gap':
    case 'malformed':
      return ' inspector__frame--fault';
    case 'reconnect':
    case 'watchdog':
    case 'retry':
      return ' inspector__frame--hold';
    case 'duplicate':
    case 'stale':
    case 'post_terminal':
      return ' inspector__frame--muted';
    // Listed rather than left to a default branch: the switch is exhaustive
    // over TraceEntry, so a new entry kind fails the build here and has to be
    // given a tone on purpose instead of silently rendering as a plain frame.
    case 'frame':
    case 'keepalive':
    case 'sentinel':
    case 'unknown_event':
    case 'replace':
    case 'cancelled':
      return '';
  }
}

/** The phase badge's own tone, reused for the readout's pilot-light dot. */
function phaseTone(phase: StreamState['phase']): 'signal' | 'hold' | 'fault' | '' {
  switch (phase) {
    case 'thinking':
    case 'streaming':
      return 'signal';
    case 'connecting':
    case 'queued':
    case 'reconnecting':
    case 'stalled':
      return 'hold';
    case 'error':
      return 'fault';
    case 'idle':
    case 'done':
    case 'cancelled':
      return '';
  }
}

function describeEntry(entry: TraceEntry): string {
  switch (entry.kind) {
    case 'frame':
      return `${entry.type}${entry.id === null ? '' : ` #${String(entry.id)}`}`;
    case 'keepalive':
      return 'keepalive';
    case 'sentinel':
      return 'sentinel';
    case 'duplicate':
      return `duplicate #${String(entry.id)}`;
    case 'stale':
      return `stale #${String(entry.id)}`;
    case 'gap':
      return `gap ${String(entry.fromId)} → ${String(entry.toId)}${entry.permanent ? ' (permanent)' : ''}`;
    case 'malformed':
      return `malformed (${String(entry.failures)} at this id)`;
    case 'unknown_event':
      return `unknown event: ${entry.event}`;
    case 'post_terminal':
      return `discarded after terminal: ${entry.description}`;
    case 'replace':
      return `replace, ${String(entry.charactersRemoved)} chars removed`;
    case 'reconnect':
      return `reconnect attempt ${String(entry.attempt)} from #${String(entry.lastEventId)}`;
    case 'watchdog':
      return `${entry.watchdog} watchdog fired`;
    case 'cancelled':
      return 'cancelled';
    case 'retry':
      return 'retry';
  }
}

type FilterKind = 'all' | 'frames' | 'faults' | 'network';

const FILTER_LABEL: Record<FilterKind, string> = {
  all: 'All',
  frames: 'Frames',
  faults: 'Faults',
  network: 'Network',
};

function matchesFilter(entry: TraceEntry, filter: FilterKind): boolean {
  switch (filter) {
    case 'all':
      return true;
    case 'frames':
      return entry.kind === 'frame';
    case 'faults':
      return (
        entry.kind === 'gap' ||
        entry.kind === 'malformed' ||
        entry.kind === 'replace' ||
        entry.kind === 'unknown_event'
      );
    case 'network':
      return (
        entry.kind === 'reconnect' ||
        entry.kind === 'watchdog' ||
        entry.kind === 'retry' ||
        entry.kind === 'cancelled'
      );
  }
}

/**
 * Three sections in one panel: Readout, Metrics, Frame list. No landmark of
 * its own: it is meant to be mounted inside the `<aside>` that carries the
 * "Inspector" label, so the three read as one instrument rather than three
 * separately labelled regions.
 */
export function Inspector({ state }: { state: StreamState }): ReactElement {
  const [showTrace, setShowTrace] = useState(false);
  const [filter, setFilter] = useState<FilterKind>('all');
  const [clearedAt, setClearedAt] = useState<number | null>(null);
  const [copied, setCopied] = useState(false);

  const activeTimeline =
    clearedAt === null ? state.timeline : state.timeline.filter((e) => e.at >= clearedAt);

  // Indexed against the append-only timeline, not the filtered/reversed
  // display order: a row's index never changes once assigned, which is what
  // keeps the entry-arrival animation scoped to the one row that is actually
  // new instead of replaying across the whole list on every arrival.
  const indexed = activeTimeline.map((entry, index) => ({ entry, index }));
  const filteredIndexed = indexed.filter(({ entry }) => matchesFilter(entry, filter));
  const frameCount = filteredIndexed.filter(({ entry }) => entry.kind === 'frame').length;

  const copyTraceJson = (): void => {
    const json = JSON.stringify(activeTimeline, null, 2);
    void navigator.clipboard.writeText(json).then(() => {
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    });
  };

  const clearInspectorLog = (): void => {
    // `TraceEntry.at` is stamped from the machine's injected monotonic clock
    // (`performance.now` in the browser, see lib/environment.ts), never
    // `Date.now`. The two clocks have unrelated epochs and scales, so
    // filtering `entry.at >= clearedAt` against a `Date.now()` value could
    // never match a single entry again: the cleared log would stay empty for
    // the rest of the session no matter what ran afterwards.
    setClearedAt(now());
  };

  const rows = [...filteredIndexed].slice(-200).reverse();

  return (
    <div className="inspector">
      <section className="inspector__section" aria-label="Readout">
        <h2 className="inspector__section-heading">Readout</h2>
        <div className="inspector__phase-line">
          <span
            className={`inspector__phase-dot${phaseTone(state.phase) === '' ? '' : ` inspector__phase-dot--${phaseTone(state.phase)}`}`}
            aria-hidden="true"
          />
          <span className="mono inspector__phase-value">{state.phase}</span>
        </div>
        <div className="kv">
          <span className="kv__key">cursor</span>
          <span className="kv__value">{state.lastEventId}</span>
        </div>
        <div className="kv">
          <span className="kv__key">reconnects</span>
          <span className="kv__value">{state.reconnects}</span>
        </div>
        <div className={`kv${state.streamId === null ? ' kv--last' : ''}`}>
          <span className="kv__key">frames seen</span>
          <span className="kv__value">{frameCount}</span>
        </div>
        {state.streamId !== null && (
          <div className="kv kv--last">
            <span className="kv__key">stream id</span>
            <span className="kv__value">{state.streamId}</span>
          </div>
        )}
      </section>

      <section className="inspector__section" aria-label="Metrics">
        <h2 className="inspector__section-heading">Metrics</h2>
        <MetricsStrip state={state} />
      </section>

      <section className="inspector__section inspector__section--frames" aria-label="Frame list">
        <div className="inspector__frames-header">
          <h2 className="inspector__section-heading inspector__section-heading--plain">
            Frame list
          </h2>
          {frameCount > 0 && <span className="mono inspector__count">{frameCount}</span>}
        </div>

        <div className="inspector__filters" role="group" aria-label="Filter frame entries">
          {(['all', 'frames', 'faults', 'network'] as const).map((kind) => (
            <button
              key={kind}
              type="button"
              className={`inspector__filter-btn${filter === kind ? ' inspector__filter-btn--active' : ''}`}
              aria-pressed={filter === kind}
              onClick={() => {
                setFilter(kind);
              }}
            >
              {FILTER_LABEL[kind]}
            </button>
          ))}
        </div>

        <div className="inspector__actions">
          {state.streamId !== null && (
            <button
              type="button"
              className="button button--quiet inspector__compare"
              onClick={() => {
                setShowTrace((current) => !current);
              }}
            >
              {showTrace ? 'Hide server trace' : 'Compare with server'}
            </button>
          )}
          {activeTimeline.length > 0 && (
            <>
              <button
                type="button"
                className="button button--quiet"
                onClick={copyTraceJson}
                aria-label={copied ? 'Trace copied' : 'Copy trace as JSON'}
              >
                {copied ? 'Copied' : 'Copy JSON'}
              </button>
              <button type="button" className="button button--quiet" onClick={clearInspectorLog}>
                Clear log
              </button>
            </>
          )}
        </div>

        {showTrace && state.streamId !== null && (
          <TraceComparison streamId={state.streamId} clientState={state} />
        )}

        {rows.length === 0 ? (
          <p className="muted inspector__empty">
            {activeTimeline.length === 0
              ? 'No frames yet. Run a scenario and every event lands here as it arrives.'
              : 'No frames match the active filter.'}
          </p>
        ) : (
          <ol className="inspector__frames" tabIndex={0} aria-label="Frame list, most recent first">
            {rows.map(({ entry, index }) => (
              <li key={index} className={`inspector__frame${entryTone(entry)}`}>
                <span className="mono inspector__frame-index">
                  {String(index + 1).padStart(3, '0')}
                </span>
                <span className="mono inspector__frame-desc">{describeEntry(entry)}</span>
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  );
}
